"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DateQualifier = exports.DatePrecision = void 0;
exports.parsePartialDate = parsePartialDate;
exports.formatPartialDate = formatPartialDate;
exports.toGedcomDate = toGedcomDate;
exports.partialDateSortKey = partialDateSortKey;
exports.precisionFromParts = precisionFromParts;
var DatePrecision;
(function (DatePrecision) {
    DatePrecision["UNKNOWN"] = "UNKNOWN";
    DatePrecision["YEAR"] = "YEAR";
    DatePrecision["MONTH_YEAR"] = "MONTH_YEAR";
    DatePrecision["DAY_MONTH"] = "DAY_MONTH";
    DatePrecision["DAY"] = "DAY";
})(DatePrecision || (exports.DatePrecision = DatePrecision = {}));
var DateQualifier;
(function (DateQualifier) {
    DateQualifier["EXACT"] = "EXACT";
    DateQualifier["ABOUT"] = "ABOUT";
    DateQualifier["BEFORE"] = "BEFORE";
    DateQualifier["AFTER"] = "AFTER";
    DateQualifier["RANGE"] = "RANGE";
})(DateQualifier || (exports.DateQualifier = DateQualifier = {}));
const MONTHS = {
    янв: 0, января: 0, январь: 0, jan: 0, january: 0,
    фев: 1, февраля: 1, февраль: 1, feb: 1, february: 1,
    мар: 2, марта: 2, март: 2, mar: 2, march: 2,
    апр: 3, апреля: 3, апрель: 3, apr: 3, april: 3,
    май: 4, мая: 4, may: 4,
    июн: 5, июня: 5, июнь: 5, jun: 5, june: 5,
    июл: 6, июля: 6, июль: 6, jul: 6, july: 6,
    авг: 7, августа: 7, август: 7, aug: 7, august: 7,
    сен: 8, сент: 8, сентября: 8, сентябрь: 8, sep: 8, september: 8,
    окт: 9, октября: 9, октябрь: 9, oct: 9, october: 9,
    ноя: 10, ноября: 10, ноябрь: 10, nov: 10, november: 10,
    дек: 11, декабря: 11, декабрь: 11, dec: 11, december: 11,
};
const RU_MONTHS = [
    'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
    'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
];
const EN_MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];
const EN_MONTHS_SHORT = [
    'JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN',
    'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC',
];
function monthIndex(token) {
    const clean = token.toLowerCase().replace(/\./g, '').trim();
    return MONTHS[clean] ?? MONTHS[clean.slice(0, 4)] ?? MONTHS[clean.slice(0, 3)];
}
function validDate(year, month, day) {
    if (month < 0 || month > 11 || day < 1 || day > 31)
        return false;
    if (year === undefined)
        return true;
    const date = new Date(Date.UTC(year, month, day));
    return (date.getUTCFullYear() === year &&
        date.getUTCMonth() === month &&
        date.getUTCDate() === day);
}
function parsePartialDate(rawInput) {
    const raw = rawInput?.trim() ?? '';
    if (!raw) {
        return { precision: DatePrecision.UNKNOWN, qualifier: DateQualifier.EXACT, raw: null };
    }
    let qualifier = DateQualifier.EXACT;
    let text = raw
        .replace(/[()]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    const lower = text.toLowerCase();
    if (/(^|\s)(abt|about|approx|около|примерно)(\s|$)/i.test(lower) || lower.includes('~')) {
        qualifier = DateQualifier.ABOUT;
        text = text
            .replace(/(^|\s)(abt|about|approx|около|примерно)(\s|$)/gi, ' ')
            .replace(/~/g, '')
            .trim();
    }
    else if (/(^|\s)(bef|before|до)(\s|$)/i.test(lower)) {
        qualifier = DateQualifier.BEFORE;
        text = text.replace(/(^|\s)(bef|before|до)(\s|$)/gi, ' ').trim();
    }
    else if (/(^|\s)(aft|after|после)(\s|$)/i.test(lower)) {
        qualifier = DateQualifier.AFTER;
        text = text.replace(/(^|\s)(aft|after|после)(\s|$)/gi, ' ').trim();
    }
    else if (/(^|\s)(bet|between|между)(\s|$)/i.test(lower) ||
        /(^|\s)(from|с)(\s).*(\s)(to|по)(\s|$)/i.test(lower) ||
        /\d{4}\s*[-–]\s*\d{4}/.test(lower)) {
        qualifier = DateQualifier.RANGE;
    }
    const yearFirst = text.match(/(\d{3,4})-(\d{1,2})-(\d{1,2})/);
    if (yearFirst) {
        const year = Number(yearFirst[1]);
        const month = Number(yearFirst[2]) - 1;
        const day = Number(yearFirst[3]);
        if (validDate(year, month, day)) {
            return { year, month: month + 1, day, precision: DatePrecision.DAY, qualifier, raw };
        }
    }
    const yearMonth = text.match(/(\d{3,4})-(\d{1,2})/);
    if (yearMonth && !text.includes('.')) {
        const year = Number(yearMonth[1]);
        const month = Number(yearMonth[2]) - 1;
        if (month >= 0 && month <= 11) {
            return { year, month: month + 1, precision: DatePrecision.MONTH_YEAR, qualifier, raw };
        }
    }
    const numeric = text.match(/(\d{1,2})\.(\d{1,2})\.(\d{3,4})/);
    if (numeric) {
        const day = Number(numeric[1]);
        const month = Number(numeric[2]) - 1;
        const year = Number(numeric[3]);
        if (validDate(year, month, day)) {
            return { year, month: month + 1, day, precision: DatePrecision.DAY, qualifier, raw };
        }
    }
    const dayMonthYear = text.match(/(\d{1,2})\s+([A-Za-zА-Яа-яЁё]+)\.?\s+(\d{3,4})/);
    if (dayMonthYear) {
        const day = Number(dayMonthYear[1]);
        const month = monthIndex(dayMonthYear[2]);
        const year = Number(dayMonthYear[3]);
        if (month !== undefined && validDate(year, month, day)) {
            return { year, month: month + 1, day, precision: DatePrecision.DAY, qualifier, raw };
        }
    }
    const monthYear = text.match(/([A-Za-zА-Яа-яЁё]+)\.?\s+(\d{3,4})/);
    if (monthYear) {
        const month = monthIndex(monthYear[1]);
        const year = Number(monthYear[2]);
        if (month !== undefined) {
            return { year, month: month + 1, precision: DatePrecision.MONTH_YEAR, qualifier, raw };
        }
    }
    const yearOnly = text.match(/\b(\d{3,4})\b/);
    if (yearOnly) {
        return {
            year: Number(yearOnly[1]),
            precision: DatePrecision.YEAR,
            qualifier,
            raw,
        };
    }
    const dayMonth = text.match(/(\d{1,2})\s+([A-Za-zА-Яа-яЁё]+)\.?/);
    if (dayMonth) {
        const day = Number(dayMonth[1]);
        const month = monthIndex(dayMonth[2]);
        if (month !== undefined && validDate(undefined, month, day)) {
            return { month: month + 1, day, precision: DatePrecision.DAY_MONTH, qualifier, raw };
        }
    }
    return { precision: DatePrecision.UNKNOWN, qualifier, raw };
}
function formatPartialDate(date, locale = 'ru') {
    if (!date)
        return '';
    const raw = date.raw?.trim();
    if (date.qualifier === DateQualifier.RANGE && raw)
        return raw;
    if (date.precision === DatePrecision.UNKNOWN)
        return raw ?? '';
    const months = locale === 'en' ? EN_MONTHS : RU_MONTHS;
    const monthName = date.month !== null && date.month !== undefined ? months[date.month - 1] : undefined;
    let value = '';
    if (date.precision === DatePrecision.DAY && date.day && monthName && date.year) {
        value = locale === 'en'
            ? `${monthName} ${date.day}, ${date.year}`
            : `${date.day} ${monthName} ${date.year}`;
    }
    else if (date.precision === DatePrecision.DAY_MONTH && date.day && monthName) {
        value = locale === 'en' ? `${monthName} ${date.day}` : `${date.day} ${monthName}`;
    }
    else if (date.precision === DatePrecision.MONTH_YEAR && monthName && date.year) {
        value = `${monthName} ${date.year}`;
    }
    else if (date.precision === DatePrecision.YEAR && date.year) {
        value = String(date.year);
    }
    else {
        value = raw ?? '';
    }
    if (!value)
        return raw ?? '';
    if (date.qualifier === DateQualifier.ABOUT) {
        return locale === 'en' ? `about ${value}` : `около ${value}`;
    }
    if (date.qualifier === DateQualifier.BEFORE) {
        return locale === 'en' ? `before ${value}` : `до ${value}`;
    }
    if (date.qualifier === DateQualifier.AFTER) {
        return locale === 'en' ? `after ${value}` : `после ${value}`;
    }
    return value;
}
function toGedcomDate(date) {
    if (!date)
        return null;
    const prefix = date.qualifier === DateQualifier.ABOUT
        ? 'ABT '
        : date.qualifier === DateQualifier.BEFORE
            ? 'BEF '
            : date.qualifier === DateQualifier.AFTER
                ? 'AFT '
                : '';
    if (date.qualifier === DateQualifier.RANGE && date.raw)
        return date.raw;
    if (date.precision === DatePrecision.DAY && date.day && date.month !== null && date.month !== undefined && date.year) {
        return `${prefix}${date.day} ${EN_MONTHS_SHORT[date.month - 1]} ${date.year}`;
    }
    if (date.precision === DatePrecision.MONTH_YEAR && date.month !== null && date.month !== undefined && date.year) {
        return `${prefix}${EN_MONTHS_SHORT[date.month - 1]} ${date.year}`;
    }
    if (date.precision === DatePrecision.YEAR && date.year) {
        return `${prefix}${date.year}`;
    }
    return date.raw ?? null;
}
function partialDateSortKey(date) {
    if (!date)
        return 0;
    const year = date.year ?? 0;
    const month = date.month !== null && date.month !== undefined ? date.month : 0;
    const day = date.day ?? 0;
    return year * 10000 + month * 100 + day;
}
function precisionFromParts(year, month, day) {
    if (year && month !== null && month !== undefined && day)
        return DatePrecision.DAY;
    if (year && month !== null && month !== undefined)
        return DatePrecision.MONTH_YEAR;
    if (month !== null && month !== undefined && day)
        return DatePrecision.DAY_MONTH;
    if (year)
        return DatePrecision.YEAR;
    return DatePrecision.UNKNOWN;
}
//# sourceMappingURL=partial-date.js.map